<template>
	<div class="project-list-page">
		<div class="project-list-toolbar">
			<portal-filter-bar :label="$t('global.filter')">
				<portal-search-field
					v-model="projectFilter"
					:label="$t('global.filter')"
					:placeholder="$t('global.filter')"
					:clear-label="$t('global.clear')"
				></portal-search-field>
				<portal-select-field
					v-model="projectStatus"
					:label="$t('projects.filter.status')"
					:options="projectStatusOptions"
				></portal-select-field>
				<template v-slot:summary>
					{{ filteredProjects.length }} {{ $t('projects.page-heading') }}
				</template>
			</portal-filter-bar>
		</div>

		<div class="project-list-content show-scrollbar" v-if="isReady">
			<div class="project-grid" v-if="filteredProjects.length">
				<router-link
					v-for="project in filteredProjects"
					:key="project.id"
					:to="`/projects/${project.id}`"
					class="project-card"
				>
					<div class="project-card-top">
						<span class="project-icon">
							<portal-icon name="folder-kanban" :size="21"></portal-icon>
						</span>
						<portal-status-pill
							:background="project.active ? 'var(--v-success_10-base)' : 'var(--v-gray_20-base)'"
							:color="project.active ? 'var(--v-success-base)' : 'var(--portal-muted)'"
						>
							{{ $t(projectStatusKey(project)) }}
						</portal-status-pill>
					</div>
					<div class="project-card-copy">
						<h2>{{ project.name }}</h2>
						<p v-if="project.dueDate">
							{{ $t('global.due') }} {{ formatProjectDueDate(project) }}
						</p>
						<p v-else>{{ $t('projects.no-due-date') }}</p>
					</div>
					<div class="project-card-action">
						<span>{{ $t('projects.overview.heading') }}</span>
						<portal-icon name="arrow-right" :size="18"></portal-icon>
					</div>
				</router-link>
			</div>
			<div class="project-list-empty" v-else>
				<span class="project-icon">
					<portal-icon name="folder-kanban" :size="22"></portal-icon>
				</span>
				<p>{{ $t('timetrack.no-projects') }}</p>
			</div>
		</div>
		<div class="project-list-loading" v-else>
			<v-progress-circular indeterminate :size="38" :width="3" color="primary"></v-progress-circular>
		</div>
	</div>
</template>

<script>
	import { isProjectSuspended, canEditProject, projectStatusKey } from '@/modules/projects/projectLifecycle';
	import { DateTime } from 'luxon';
	import ProjectService from '@/modules/projects/ProjectService';
	import PortalFilterBar from '@/components/filters/PortalFilterBar';
	import PortalSearchField from '@/components/filters/PortalSearchField';
	import PortalSelectField from '@/components/filters/PortalSelectField';

	export default {
		name: 'ProjectListPage',

		components: {
			PortalFilterBar,
			PortalSearchField,
			PortalSelectField,
		},

		data() {
			return {
				projectService: new ProjectService(),
				projects: [],
				projectFilter: '',
				projectStatus: 'Active',
				isReady: false,
			};
		},

		mounted() {
			this.projectService
				.getProjects()
				.then((res) => {
					this.projects.splice(0, this.projects.length, ...res.data);
				})
				.finally(() => {
					this.isReady = true;
				});
		},

		computed: {
			projectStatusOptions() {
				return [
					{ text: this.$t('projects.filter.active'), value: 'Active' },
					{ text: this.$t('projects.filter.complete'), value: 'Archived' },
					{ text: this.$t('projects.paused'), value: 'PAUSED' },
					{ text: this.$t('projects.canceled'), value: 'CANCELED' },
					{ text: this.$t('projects.filter.all'), value: 'All' },
				];
			},

			filteredProjects() {
				const search = this.projectFilter ? this.projectFilter.toLowerCase() : '';
				return [...this.projects]
					.filter((project) => {
						if (this.projectStatus === 'Active') return project.active === true;
						if (this.projectStatus === 'Archived') return project.active === false && !isProjectSuspended(project);
						if (['PAUSED', 'CANCELED'].includes(this.projectStatus)) return project.lifecycleStatus === this.projectStatus;
						return true;
					})
					.filter((project) => !search || (project.name && project.name.toLowerCase().includes(search)))
					.sort((a, b) => {
						if (a.active !== b.active) return a.active ? -1 : 1;
						return a.name.localeCompare(b.name);
					});
			},
		},

		methods: {
			isProjectSuspended, canEditProject, projectStatusKey,
			formatProjectDueDate(project) {
				return DateTime.fromISO(project.dueDate).toLocaleString(DateTime.DATE_MED);
			},
		},
	};
</script>

<style scoped lang="scss">
	.project-list-page {
		min-height: 100%;
		display: flex;
		flex-direction: column;
	}

	.project-list-toolbar {
		position: sticky;
		z-index: 4;
		top: 0;
		padding: 16px 16px 0;
		background: var(--portal-canvas);
	}

	.project-list-content {
		min-height: 0;
		flex: 1;
		padding: 16px;
	}

	.project-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
		gap: 14px;
	}

	.project-card {
		min-height: 210px;
		display: flex;
		flex-direction: column;
		padding: 18px;
		border: 1px solid var(--portal-border);
		border-radius: var(--portal-radius-md);
		background: var(--portal-surface);
		color: var(--portal-text);
		text-decoration: none;
		transition: transform 150ms ease, border-color 150ms ease, box-shadow 150ms ease;
	}

	.project-card:hover {
		transform: translateY(-2px);
		border-color: color-mix(in srgb, var(--v-primary-base) 34%, var(--portal-border));
		box-shadow: 0 14px 32px rgba(17, 24, 39, 0.08);
	}

	.project-card:focus-visible {
		outline: 2px solid var(--v-primary-base);
		outline-offset: 3px;
	}

	.project-card-top,
	.project-card-action {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
	}

	.project-icon {
		width: 42px;
		height: 42px;
		display: grid;
		place-items: center;
		border-radius: 13px;
		background: color-mix(in srgb, var(--v-primary-base) 9%, var(--portal-surface));
		color: var(--v-primary-base);
	}

	.project-card-copy {
		flex: 1;
		padding: 20px 0;
	}

	.project-card h2 {
		margin: 0 0 7px;
		color: var(--portal-text);
		font-size: 19px;
		font-weight: 680;
		letter-spacing: -0.02em;
	}

	.project-card p {
		margin: 0;
		color: var(--portal-muted);
		font-size: 13px;
	}

	.project-card-action {
		padding-top: 14px;
		border-top: 1px solid var(--portal-border);
		color: var(--portal-muted);
		font-size: 13px;
		font-weight: 650;
	}

	.project-card:hover .project-card-action {
		color: var(--v-primary-base);
	}

	.project-list-empty,
	.project-list-loading {
		min-height: 320px;
		display: grid;
		place-content: center;
		justify-items: center;
		gap: 12px;
		color: var(--portal-muted);
		text-align: center;
	}

	.project-list-empty p {
		margin: 0;
	}

	@media (max-width: 599px) {
		.project-list-toolbar {
			padding: 12px 12px 0;
		}

		.project-list-content {
			padding: 12px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.project-card {
			transition: none;
		}
	}
</style>
